import React, { useState } from 'react';
import CollapseChevron from './CollapseChevron';

interface CollapsibleSectionProps {
  title: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

/**
 * CollapsibleSection
 * 
 * A reusable wrapper that makes any content collapsible.
 * - Header with icon, title, and chevron
 * - Smooth collapse/expand
 * - Consistent styling
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  defaultExpanded = false,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header - clickable to toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        {/* Left side: icon + title */}
        <div className="flex items-center gap-2">
          {icon && <span className="text-base flex items-center">{icon}</span>}
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        
        {/* Right side: chevron */}
        <CollapseChevron 
          expanded={expanded} 
          onToggle={() => setExpanded(!expanded)} 
          size="sm"
        />
      </button>
      
      {/* Content - collapsible */}
      {expanded && (
        <div className="border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleSection;
